import { useEffect, useState } from "react";
import { api, formatApiErrorDetail } from "../../lib/api";
import { ImageUploader } from "./ProjectEdit";

export default function SiteContentEdit() {
  const [content, setContent] = useState(null);
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState("");
  const [error, setError] = useState("");

  useEffect(() => {
    api.get("/site-content").then((res) => setContent(res.data));
  }, []);

  if (!content) return <div className="p-10 text-[#5d5f64]">Loading…</div>;

  const set = (path, value) => {
    setContent((prev) => {
      const next = structuredClone(prev);
      let ref = next;
      const keys = path.split(".");
      for (let i = 0; i < keys.length - 1; i++) {
        if (ref[keys[i]] === undefined || ref[keys[i]] === null) ref[keys[i]] = {};
        ref = ref[keys[i]];
      }
      ref[keys[keys.length - 1]] = value;
      return next;
    });
  };

  const get = (path) => {
    const keys = path.split(".");
    let ref = content;
    for (const k of keys) {
      if (ref == null) return "";
      ref = ref[k];
    }
    return ref ?? "";
  };

  const uploadFor = async (path, file) => {
    const form = new FormData();
    form.append("file", file);
    const { data } = await api.post("/uploads", form, {
      headers: { "Content-Type": "multipart/form-data" },
    });
    set(path, data.path);
  };

  const updateServiceItem = (i, key, lang, value) => {
    const items = [...content.services.items];
    if (lang) {
      items[i] = { ...items[i], [key]: { ...items[i][key], [lang]: value } };
    } else {
      items[i] = { ...items[i], [key]: value };
    }
    set("services.items", items);
  };

  const updateExpertiseItem = (i, key, lang, value) => {
    const items = [...content.expertise.items];
    if (lang) {
      items[i] = { ...items[i], [key]: { ...items[i][key], [lang]: value } };
    } else {
      items[i] = { ...items[i], [key]: value };
    }
    set("expertise.items", items);
  };

  const updateStat = (i, field, lang, value) => {
    const stats = [...content.who_we_are.stats];
    if (field === "value") stats[i] = { ...stats[i], value };
    else stats[i] = { ...stats[i], label: { ...stats[i].label, [lang]: value } };
    set("who_we_are.stats", stats);
  };

  const onSave = async () => {
    setSaving(true);
    setError("");
    setMessage("");
    try {
      await api.put("/site-content", content);
      setMessage("Saved successfully.");
      setTimeout(() => setMessage(""), 2500);
    } catch (e) {
      setError(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="p-10 max-w-[1100px]" data-testid="admin-content-edit">
      <div className="flex items-end justify-between mb-10 flex-wrap gap-4">
        <div>
          <div className="text-xs uppercase tracking-wider text-[#5d5f64] mb-2">Homepage</div>
          <h1 className="font-display font-semibold text-[#16181c] text-3xl tracking-[-0.02em]">
            Edit content
          </h1>
        </div>
        <div className="flex items-center gap-4">
          {message && <span className="text-sm text-[#16181c]">{message}</span>}
          <button
            onClick={onSave}
            disabled={saving}
            className="bg-[#16181c] text-white px-6 py-3 font-medium hover:bg-[#5d5f64] disabled:opacity-60"
            data-testid="content-save"
          >
            {saving ? "Saving…" : "Save changes"}
          </button>
        </div>
      </div>

      {error && (
        <div className="text-sm text-[#16181c] bg-[#f3f3f0] border border-[#e3e3e1] px-4 py-3 mb-6">
          {error}
        </div>
      )}

      <div className="space-y-8">
        <Card title="Hero">
          <Bilingual label="Headline" pathPrefix="hero.headline" get={get} set={set} testid="hero-headline" multiline />
          <Bilingual label="Supporting line" pathPrefix="hero.supporting" get={get} set={set} testid="hero-supporting" multiline />
          <Bilingual label="Image credit" pathPrefix="hero.image_credit" get={get} set={set} testid="hero-credit" />
          <FieldLabel>Hero background image</FieldLabel>
          <ImageUploader
            value={content.hero?.image}
            onChange={(v) => set("hero.image", v)}
            onFile={(f) => uploadFor("hero.image", f)}
            testid="hero-image"
          />
        </Card>

        <Card title="Statement">
          <Bilingual label="Eyebrow" pathPrefix="statement.eyebrow" get={get} set={set} testid="statement-eyebrow" />
          <Bilingual label="Headline (use **word** for emphasis)" pathPrefix="statement.headline" get={get} set={set} testid="statement-headline" multiline />
          <Bilingual label="Supporting paragraph" pathPrefix="statement.paragraph" get={get} set={set} testid="statement-paragraph" multiline />
          <Bilingual label="Link text" pathPrefix="statement.link_text" get={get} set={set} testid="statement-link" />
        </Card>

        <Card title="Projects section heading">
          <Bilingual label="Eyebrow" pathPrefix="projects_section.eyebrow" get={get} set={set} testid="projects-eyebrow" />
          <Bilingual label="Heading" pathPrefix="projects_section.heading" get={get} set={set} testid="projects-heading" />
          <Bilingual label="View all link" pathPrefix="projects_section.view_all" get={get} set={set} testid="projects-viewall" />
        </Card>

        <Card title="Services">
          <Bilingual label="Eyebrow" pathPrefix="services.eyebrow" get={get} set={set} testid="services-eyebrow" />
          <Bilingual label="Heading" pathPrefix="services.heading" get={get} set={set} testid="services-heading" />
          {(content.services?.items || []).map((item, i) => (
            <div key={i} className="border-t border-[#e3e3e1] pt-6 mt-6">
              <div className="text-xs uppercase tracking-wider text-[#5d5f64] mb-4">
                Service #{i + 1}
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <TextField
                  label="Title (Indonesian)"
                  value={item.title?.id || ""}
                  onChange={(v) => updateServiceItem(i, "title", "id", v)}
                />
                <TextField
                  label="Title (English)"
                  value={item.title?.en || ""}
                  onChange={(v) => updateServiceItem(i, "title", "en", v)}
                />
              </div>
              <TextField
                label="SBU code"
                value={item.sbu || ""}
                onChange={(v) => updateServiceItem(i, "sbu", null, v)}
              />
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <TextField
                  label="Description (ID)"
                  multiline
                  value={item.description?.id || ""}
                  onChange={(v) => updateServiceItem(i, "description", "id", v)}
                />
                <TextField
                  label="Description (EN)"
                  multiline
                  value={item.description?.en || ""}
                  onChange={(v) => updateServiceItem(i, "description", "en", v)}
                />
              </div>
            </div>
          ))}
        </Card>

        <Card title="Who We Are">
          <Bilingual label="Eyebrow" pathPrefix="who_we_are.eyebrow" get={get} set={set} testid="who-eyebrow" />
          <Bilingual label="Statement" pathPrefix="who_we_are.statement" get={get} set={set} testid="who-statement" multiline />
          <Bilingual label="Paragraph" pathPrefix="who_we_are.paragraph" get={get} set={set} testid="who-paragraph" multiline />
          <FieldLabel>Image</FieldLabel>
          <ImageUploader
            value={content.who_we_are?.image}
            onChange={(v) => set("who_we_are.image", v)}
            onFile={(f) => uploadFor("who_we_are.image", f)}
            testid="who-image"
          />
          <div className="border-t border-[#e3e3e1] mt-6 pt-6">
            <div className="text-xs uppercase tracking-wider text-[#5d5f64] mb-4">Stats</div>
            {(content.who_we_are?.stats || []).map((s, i) => (
              <div key={i} className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-4">
                <TextField label="Value" value={s.value} onChange={(v) => updateStat(i, "value", null, v)} />
                <TextField
                  label="Label (ID)"
                  value={s.label?.id || ""}
                  onChange={(v) => updateStat(i, "label", "id", v)}
                />
                <TextField
                  label="Label (EN)"
                  value={s.label?.en || ""}
                  onChange={(v) => updateStat(i, "label", "en", v)}
                />
              </div>
            ))}
          </div>
        </Card>

        <Card title="Expertise">
          <Bilingual label="Eyebrow" pathPrefix="expertise.eyebrow" get={get} set={set} testid="expertise-eyebrow" />
          <Bilingual label="Heading" pathPrefix="expertise.heading" get={get} set={set} testid="expertise-heading" />
          {(content.expertise?.items || []).map((item, i) => (
            <div key={i} className="border-t border-[#e3e3e1] pt-6 mt-6">
              <div className="flex items-center justify-between mb-4">
                <div className="text-xs uppercase tracking-wider text-[#5d5f64]">
                  Discipline #{i + 1}
                </div>
                <button
                  type="button"
                  onClick={() => {
                    if (!window.confirm("Remove this discipline?")) return;
                    const items = [...content.expertise.items];
                    items.splice(i, 1);
                    set("expertise.items", items);
                  }}
                  className="text-xs text-[#5d5f64] hover:text-[#16181c]"
                  data-testid={`expertise-remove-${i}`}
                >
                  Remove
                </button>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <TextField
                  label="Title (ID)"
                  value={item.title?.id || ""}
                  onChange={(v) => updateExpertiseItem(i, "title", "id", v)}
                />
                <TextField
                  label="Title (EN)"
                  value={item.title?.en || ""}
                  onChange={(v) => updateExpertiseItem(i, "title", "en", v)}
                />
              </div>
              <TextField
                label="SBU code"
                value={item.sbu || ""}
                onChange={(v) => updateExpertiseItem(i, "sbu", null, v)}
              />
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <TextField
                  label="Description (ID)"
                  multiline
                  value={item.description?.id || ""}
                  onChange={(v) => updateExpertiseItem(i, "description", "id", v)}
                />
                <TextField
                  label="Description (EN)"
                  multiline
                  value={item.description?.en || ""}
                  onChange={(v) => updateExpertiseItem(i, "description", "en", v)}
                />
              </div>
            </div>
          ))}
          <button
            type="button"
            onClick={() => {
              const items = [...(content.expertise?.items || [])];
              items.push({
                title: { id: "", en: "" },
                description: { id: "", en: "" },
                sbu: "",
              });
              set("expertise.items", items);
            }}
            className="mt-6 text-sm border border-[#e3e3e1] bg-white px-4 py-2 hover:bg-[#f3f3f0]"
            data-testid="expertise-add"
          >
            + Add discipline
          </button>
        </Card>

        <Card title="CTA band">
          <Bilingual label="Eyebrow" pathPrefix="cta.eyebrow" get={get} set={set} testid="cta-eyebrow" />
          <Bilingual label="Headline" pathPrefix="cta.headline" get={get} set={set} testid="cta-headline" multiline />
          <Bilingual label="Supporting line" pathPrefix="cta.supporting" get={get} set={set} testid="cta-supporting" multiline />
          <Bilingual label="Button text" pathPrefix="cta.button_text" get={get} set={set} testid="cta-button" />
          <div className="text-xs text-[#5d5f64] pt-3 border-t border-[#e3e3e1]">
            Contact details (address, email, phone, WhatsApp, LinkedIn) live in{" "}
            <a href="/admin/logo" className="text-[#16181c] underline">Brand &amp; contact</a>.
          </div>
        </Card>

        <Card title="Footer">
          <Bilingual label="Brand blurb" pathPrefix="footer.blurb" get={get} set={set} testid="footer-blurb" multiline />
          <TextField
            label="Copyright"
            value={content.footer?.copyright || ""}
            onChange={(v) => set("footer.copyright", v)}
          />
        </Card>
      </div>

      <div className="mt-10 flex items-center gap-4">
        <button
          onClick={onSave}
          disabled={saving}
          className="bg-[#16181c] text-white px-6 py-3 font-medium hover:bg-[#5d5f64] disabled:opacity-60"
          data-testid="content-save-bottom"
        >
          {saving ? "Saving…" : "Save changes"}
        </button>
        {message && <span className="text-sm text-[#16181c]">{message}</span>}
      </div>

      <style>{`
        .ce-input {
          width: 100%;
          background: #ffffff;
          border: 1px solid #e3e3e1;
          padding: 10px 12px;
          font-family: "Inter", sans-serif;
          font-size: 14px;
          color: #16181c;
        }
        .ce-input:focus { outline: none; border-color: #16181c; }
      `}</style>
    </div>
  );
}

function Card({ title, children }) {
  return (
    <section className="bg-white border border-[#e3e3e1] p-6">
      <div className="text-xs uppercase tracking-wider text-[#5d5f64] mb-5">{title}</div>
      <div className="space-y-4">{children}</div>
    </section>
  );
}

function FieldLabel({ children }) {
  return <div className="text-xs font-medium text-[#5d5f64] mt-3 mb-2">{children}</div>;
}

function TextField({ label, value, onChange, multiline, testid }) {
  return (
    <label className="block">
      <span className="block text-xs font-medium text-[#5d5f64] mb-2">{label}</span>
      {multiline ? (
        <textarea
          rows={3}
          className="ce-input"
          value={value || ""}
          onChange={(e) => onChange(e.target.value)}
          data-testid={testid}
        />
      ) : (
        <input
          type="text"
          className="ce-input"
          value={value || ""}
          onChange={(e) => onChange(e.target.value)}
          data-testid={testid}
        />
      )}
    </label>
  );
}

function Bilingual({ label, pathPrefix, get, set, testid, multiline }) {
  return (
    <div>
      <div className="text-xs font-medium text-[#5d5f64] mb-2">{label}</div>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div>
          <div className="text-[11px] text-[#5d5f64] mb-1">ID</div>
          {multiline ? (
            <textarea
              rows={3}
              className="ce-input"
              value={get(`${pathPrefix}.id`) || ""}
              onChange={(e) => set(`${pathPrefix}.id`, e.target.value)}
              data-testid={`${testid}-id`}
            />
          ) : (
            <input
              type="text"
              className="ce-input"
              value={get(`${pathPrefix}.id`) || ""}
              onChange={(e) => set(`${pathPrefix}.id`, e.target.value)}
              data-testid={`${testid}-id`}
            />
          )}
        </div>
        <div>
          <div className="text-[11px] text-[#5d5f64] mb-1">EN</div>
          {multiline ? (
            <textarea
              rows={3}
              className="ce-input"
              value={get(`${pathPrefix}.en`) || ""}
              onChange={(e) => set(`${pathPrefix}.en`, e.target.value)}
              data-testid={`${testid}-en`}
            />
          ) : (
            <input
              type="text"
              className="ce-input"
              value={get(`${pathPrefix}.en`) || ""}
              onChange={(e) => set(`${pathPrefix}.en`, e.target.value)}
              data-testid={`${testid}-en`}
            />
          )}
        </div>
      </div>
    </div>
  );
}
